/**
 * Structured project fields are stored in Notes as purpose text + a base64 JSON block,
 * so the API only needs Name + Notes while we preserve department, supervisor, team, etc.
 */

function utf8ToBase64(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch {
    return "";
  }
}

function base64ToUtf8(b64) {
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return "";
  }
}

const META_START = "<<<HRM_PROJECT_META>>>";
const META_END = "<<<END_HRM_PROJECT_META>>>";

/** Keys where an empty array is meaningful (e.g. all linked teams removed). */
const META_ALLOW_EMPTY_ARRAY = new Set(["teamIds", "newTeamMemberUserIds"]);

function cleanMeta(meta) {
  if (!meta || typeof meta !== "object") return null;
  const out = {};
  for (const [k, v] of Object.entries(meta)) {
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v) && v.length === 0 && !META_ALLOW_EMPTY_ARRAY.has(k)) continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

export function buildProjectNotes(purpose, meta) {
  const p = (purpose || "").trim();
  const cleaned = cleanMeta(meta);
  if (!cleaned) return p;
  const m = { v: 1, ...cleaned };
  const json = JSON.stringify(m);
  if (!json || json === '{"v":1}') return p;
  const b64 = utf8ToBase64(json);
  if (!b64) return p;
  return `${p}\n\n${META_START}\n${b64}\n${META_END}`;
}

export function parseProjectNotes(notes) {
  const raw = notes == null ? "" : String(notes);
  const i = raw.indexOf(META_START);
  if (i === -1) {
    return { purpose: raw.trim(), meta: {} };
  }
  const purpose = raw.slice(0, i).trim();
  const j = raw.indexOf(META_END, i);
  const b64 =
    j === -1
      ? raw.slice(i + META_START.length).trim()
      : raw.slice(i + META_START.length, j).trim();
  try {
    const parsed = JSON.parse(base64ToUtf8(b64));
    const meta = parsed && typeof parsed === "object" ? parsed : {};
    delete meta.v;
    return { purpose, meta };
  } catch {
    return { purpose: raw.trim(), meta: {} };
  }
}

export function displayPurposeFromNotes(notes) {
  return parseProjectNotes(notes).purpose;
}

/** Card/list: prefer structured description in meta, else purpose text. */
export function displayDescriptionFromNotes(notes) {
  const { purpose, meta } = parseProjectNotes(notes);
  const d = meta && typeof meta.description === "string" ? meta.description.trim() : "";
  if (d) return d;
  return (purpose || "").trim();
}

function timelinePercentFromStartEnd(startRaw, endRaw) {
  if (!startRaw || !endRaw) return null;
  const start = new Date(startRaw);
  const end = new Date(endRaw);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const span = end.getTime() - start.getTime();
  if (span <= 0) return 100;
  const now = Date.now();
  if (now <= start.getTime()) return 0;
  if (now >= end.getTime()) return 100;
  return Math.min(100, Math.max(0, Math.round(((now - start.getTime()) / span) * 100)));
}

/** Progress 0–100 from start/end in meta vs today; 0 if dates missing or invalid. */
export function getTimelineProgressFromNotes(notes) {
  const { meta } = parseProjectNotes(notes);
  const p = timelinePercentFromStartEnd(meta.startDate, meta.endDate);
  return p == null ? 0 : p;
}

/**
 * Elapsed share of the schedule window (start→end) vs today — not task “% complete”.
 * Uses API `startDate` / `endDate` when present, else notes meta.
 */
export function getTimelinePercentFromRow(row) {
  if (!row || typeof row !== "object") return 0;
  const startRaw = row.startDate ?? row.StartDate;
  const endRaw = row.endDate ?? row.EndDate ?? row.expectedEndDate ?? row.ExpectedEndDate;
  const fromApi = timelinePercentFromStartEnd(startRaw, endRaw);
  if (fromApi != null) return fromApi;
  return getTimelineProgressFromNotes(row.notes ?? "");
}

/** Prefer API title, then legacy name. */
export function getProjectDisplayTitle(row) {
  if (!row || typeof row !== "object") return "";
  const t = row.title ?? row.Title ?? row.name ?? row.Name;
  return t != null ? String(t).trim() : "";
}

/** Prefer API description / purpose, else notes-derived description. */
export function getProjectDisplayDescription(row) {
  if (!row || typeof row !== "object") return "";
  const d = row.description ?? row.Description;
  if (d != null && String(d).trim() !== "") return String(d).trim();
  const p = row.purpose ?? row.Purpose;
  if (p != null && String(p).trim() !== "") return String(p).trim();
  return displayDescriptionFromNotes(row.notes ?? "");
}

/** Stored in meta.workflowStatus / API Status string */
export const WORKFLOW_STATUS = {
  PLANNING: "planning",
  REVIEW: "review",
  ON_HOLD: "onHold",
  IN_PROGRESS: "inProgress",
  ACTIVE: "active",
  COMPLETED: "completed",
  INACTIVE: "inactive",
};

/** How the project links to a Teams record in project meta */
export const INITIAL_TEAM_MODE = {
  NONE: "none",
  EXISTING: "existing",
  NEW: "new",
};

const VALID_WORKFLOW = new Set([
  WORKFLOW_STATUS.PLANNING,
  WORKFLOW_STATUS.REVIEW,
  WORKFLOW_STATUS.ON_HOLD,
  WORKFLOW_STATUS.IN_PROGRESS,
  WORKFLOW_STATUS.ACTIVE,
  WORKFLOW_STATUS.COMPLETED,
  WORKFLOW_STATUS.INACTIVE,
]);

/** Maps API / stored status to form dropdown value (UI uses In progress, not Active). */
export function workflowStatusToFormKey(raw) {
  const normalized = normalizeApiStatusKey(raw);
  if (normalized === WORKFLOW_STATUS.ACTIVE) return WORKFLOW_STATUS.IN_PROGRESS;
  if (normalized) return normalized;
  if (raw === WORKFLOW_STATUS.ACTIVE || raw === "active" || raw === "Active") return WORKFLOW_STATUS.IN_PROGRESS;
  return WORKFLOW_STATUS.IN_PROGRESS;
}

/** Maps form workflow value to API Status string (camelCase). */
export function workflowStatusToApiStatus(workflowStatus) {
  const raw =
    workflowStatus == null || workflowStatus === "" ? WORKFLOW_STATUS.IN_PROGRESS : String(workflowStatus).trim();
  const compact = raw.toLowerCase().replace(/\s+/g, "");
  if (
    compact === "inprogress" ||
    raw === WORKFLOW_STATUS.IN_PROGRESS ||
    raw === WORKFLOW_STATUS.ACTIVE ||
    compact === "active"
  ) {
    return WORKFLOW_STATUS.ACTIVE;
  }
  if (VALID_WORKFLOW.has(raw)) return raw;
  return WORKFLOW_STATUS.ACTIVE;
}

/** Map API numeric priority 0–3 to form keys; invalid → null. */
export function priorityKeyFromApi(n) {
  if (n == null || n === "") return null;
  const i = Number(n);
  if (!Number.isFinite(i) || i < 0 || i > 3) return null;
  return ["low", "medium", "high", "critical"][i];
}

/** Workflow for edit form: prefer API `status`, else notes meta. */
export function getWorkflowStatusForForm(row) {
  if (!row || typeof row !== "object") return WORKFLOW_STATUS.IN_PROGRESS;
  const apiRaw = row.status ?? row.Status;
  if (apiRaw != null && apiRaw !== "") {
    return workflowStatusToFormKey(apiRaw);
  }
  const { meta } = parseProjectNotes(row.notes ?? "");
  return getWorkflowStatusFromMeta(meta);
}

function normalizeApiStatusKey(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const compact = s.toLowerCase().replace(/\s+/g, "");
  const map = {
    planning: WORKFLOW_STATUS.PLANNING,
    review: WORKFLOW_STATUS.REVIEW,
    onhold: WORKFLOW_STATUS.ON_HOLD,
    inprogress: WORKFLOW_STATUS.IN_PROGRESS,
    active: WORKFLOW_STATUS.IN_PROGRESS,
    completed: WORKFLOW_STATUS.COMPLETED,
    inactive: WORKFLOW_STATUS.INACTIVE,
  };
  if (map[compact]) return map[compact];
  if (VALID_WORKFLOW.has(s)) return s;
  return null;
}

/** Employee / member count on list DTOs (shape varies). */
export function getProjectAssociatedCount(row) {
  if (!row || typeof row !== "object") return 0;
  const n =
    row.associatedEmployeeCount ??
    row.AssociatedEmployeeCount ??
    row.userCount ??
    row.UserCount;
  if (n == null || n === "") return 0;
  const num = Number(n);
  return Number.isFinite(num) ? num : 0;
}

export function getWorkflowStatusFromMeta(meta) {
  if (!meta || typeof meta !== "object") return WORKFLOW_STATUS.IN_PROGRESS;
  let w = meta.workflowStatus ?? meta.status;
  if (w === "hold") w = WORKFLOW_STATUS.ON_HOLD;
  if (w === WORKFLOW_STATUS.ACTIVE || w === "active" || w === "Active") w = WORKFLOW_STATUS.IN_PROGRESS;
  if (w === "in progress" || w === "inProgress" || w === "InProgress") w = WORKFLOW_STATUS.IN_PROGRESS;
  if (w === "completed" || w === "Completed") w = WORKFLOW_STATUS.COMPLETED;
  if (VALID_WORKFLOW.has(w)) return w === WORKFLOW_STATUS.ACTIVE ? WORKFLOW_STATUS.IN_PROGRESS : w;
  return WORKFLOW_STATUS.IN_PROGRESS;
}

/**
 * List / filter key: `deactivated` when API disabled the record; else workflow status.
 * Prefers API `status` when the list payload includes it.
 */
export function getProjectStatusFilterKey(row) {
  if (!row || row.isActive === false) return "deactivated";
  const fromApi = normalizeApiStatusKey(row.status ?? row.Status);
  if (fromApi) return fromApi === WORKFLOW_STATUS.ACTIVE ? WORKFLOW_STATUS.IN_PROGRESS : fromApi;
  const { meta } = parseProjectNotes(row.notes);
  return getWorkflowStatusFromMeta(meta);
}

/** Sort order index (lower = first when ascending) */
export function getProjectStatusSortIndex(row) {
  const k = getProjectStatusFilterKey(row);
  const order = {
    deactivated: 0,
    planning: 1,
    review: 2,
    onHold: 3,
    inProgress: 4,
    completed: 5,
    inactive: 6,
  };
  return order[k] ?? 99;
}

/** For table Label: color + translated label */
export function getProjectStatusPresentation(row, t) {
  const key = getProjectStatusFilterKey(row);
  if (key === "deactivated") {
    return { key, color: "grey", label: t("app.projects.statusDeactivated") };
  }
  if (key === "planning") {
    return { key, color: "blue", label: t("app.projects.workflowStatusPlanning") };
  }
  if (key === "review") {
    return { key, color: "violet", label: t("app.projects.workflowStatusReview") };
  }
  if (key === "onHold") {
    return { key, color: "yellow", label: t("app.projects.workflowStatusOnHold") };
  }
  if (key === "inProgress") {
    return { key, color: "teal", label: t("app.projects.workflowStatusInProgress") };
  }
  if (key === "completed") {
    return { key, color: "olive", label: t("app.projects.workflowStatusCompleted") };
  }
  if (key === "inactive") {
    return { key, color: "orange", label: t("app.projects.workflowStatusInactive") };
  }
  return { key: "inProgress", color: "teal", label: t("app.projects.workflowStatusInProgress") };
}

/** List DTO numeric priority 0–3 → label + Semantic color (matches create payload). */
export function getPriorityPresentation(row, t) {
  const pr = row?.priority ?? row?.Priority;
  if (pr == null || pr === "") return null;
  const n = Number(pr);
  if (!Number.isFinite(n) || n < 0 || n > 3) return null;
  const colors = ["grey", "blue", "orange", "red"];
  const labels = [
    t("app.projects.form.priorityLow"),
    t("app.projects.form.priorityMedium"),
    t("app.projects.form.priorityHigh"),
    t("app.projects.form.priorityCritical"),
  ];
  const keys = ["low", "medium", "high", "critical"];
  return { key: keys[n], color: colors[n], label: labels[n] };
}
